import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiBarChart2, FiSearch } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EmployeeTimesheetDetail from "../../components/timesheet/dashboard/EmployeeTimesheetDetail";
import EmployeeTimesheetTable from "../../components/timesheet/dashboard/EmployeeTimesheetTable";
import TimesheetDashboardToolbar from "../../components/timesheet/dashboard/TimesheetDashboardToolbar";
import TimesheetSummaryCards from "../../components/timesheet/dashboard/TimesheetSummaryCards";
import {
  fetchEmployeeTimesheetSummary,
  fetchTimesheetDashboardExportData,
  fetchTimesheetDashboardSummary,
  fetchTimesheetDashboardUsers,
  listTimesheetClients,
} from "../../services/timesheetApi";
import {
  formatHours,
  getEntityId,
  getRangeForView,
  shiftAnchorDate,
  TIMESHEET_VIEW,
} from "../../utils/timesheetUtils";
import { exportTimesheetWorkbook } from "../../utils/timesheetExport";
import {
  readTimesheetCache,
  writeTimesheetCache,
} from "../../utils/timesheetCache";
import { isAdmin } from "../../utils/buttonVisibility";

const initialFilters = {
  search: "",
  clientId: "",
  department: "",
  team: "",
};

const DASHBOARD_CLIENTS_CACHE_KEY = "timesheetClients";
const TIMESHEET_CLIENTS_CACHE_TTL = 60 * 60 * 1000;
const DASHBOARD_ACCESS_CACHE_SCOPE = "dashboard-access";
const DASHBOARD_SUMMARY_CACHE_SCOPE = "dashboard-summary";
const DASHBOARD_DETAIL_CACHE_SCOPE = "dashboard-detail";
// scope จาก /timesheets/dashboard/users ที่หมายถึงเห็นทั้งองค์กร
const ORGANIZATION_SCOPES = ["all", "organization", "org", "company"];

const formatErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 403) {
    return "คุณไม่มีสิทธิ์ดู Timesheet นี้";
  }
  if (status === 404) {
    return "ไม่พบข้อมูล Timesheet";
  }
  if (status === 401) {
    return "เซสชันของคุณหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง";
  }
  return error?.response?.data?.message || "ไม่สามารถโหลดข้อมูล Timesheet ได้";
};

const TimesheetDashboardPage = () => {
  const navigate = useNavigate();
  const { userId: routeUserId = "" } = useParams();

  const [viewMode, setViewMode] = useState(TIMESHEET_VIEW.WEEK);
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [permissionError, setPermissionError] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dashboardAccess, setDashboardAccess] = useState({
    canViewDashboard: false,
    scope: "self",
    data: [],
  });
  const [summary, setSummary] = useState({
    range: null,
    totalUsers: 0,
    totalHours: 0,
    users: [],
  });
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [clients, setClients] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const employeeDetailRef = useRef(null);
  const permissionRequestSequenceRef = useRef(0);
  const summaryRequestSequenceRef = useRef(0);
  const detailRequestSequenceRef = useRef(0);

  const range = useMemo(
    () => getRangeForView(viewMode, anchorDate),
    [anchorDate, viewMode]
  );

  const visibleUserMap = useMemo(() => {
    return (dashboardAccess.data || []).reduce((acc, user) => {
      acc[getEntityId(user._id)] = user;
      return acc;
    }, {});
  }, [dashboardAccess.data]);

  const visibleUserOptions = useMemo(() => {
    const users = dashboardAccess.data || [];
    const uniqueValues = (key) =>
      Array.from(
        new Set(
          users
            .map((user) => String(user?.[key] || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));

    return {
      departments: uniqueValues("department"),
      teams: uniqueValues("team"),
    };
  }, [dashboardAccess.data]);

  const enrichedUsers = useMemo(() => {
    return (summary.users || []).map((user) => {
      const normalizedUserId = getEntityId(user.userId || user._id);
      const meta = visibleUserMap[normalizedUserId] || {};
      return {
        ...user,
        userId: normalizedUserId,
        department: user.department || meta.department || "",
        team: user.team || meta.team || "",
        username: user.username || meta.username || "",
      };
    });
  }, [summary.users, visibleUserMap]);

  const filteredUsers = useMemo(() => {
    return enrichedUsers.filter((user) => {
      const search = filters.search.trim().toLowerCase();
      const matchesSearch = search
        ? String(user.username || "").toLowerCase().includes(search)
        : true;
      const matchesDepartment = filters.department
        ? user.department === filters.department
        : true;
      const matchesTeam = filters.team ? user.team === filters.team : true;

      return (
        matchesSearch &&
        matchesDepartment &&
        matchesTeam
      );
    });
  }, [enrichedUsers, filters]);

  const employeePageCount = Math.max(1, Math.ceil(filteredUsers.length / 10));
  const pagedUsers = useMemo(() => {
    const start = (employeePage - 1) * 10;
    return filteredUsers.slice(start, start + 10);
  }, [employeePage, filteredUsers]);

  const filteredMetrics = useMemo(() => {
    const totalEmployees = filteredUsers.length;
    const totalHours = filteredUsers.reduce(
      (sum, user) => sum + Number(user.totalHours || 0),
      0
    );
    const averageHours =
      totalEmployees > 0 ? totalHours / totalEmployees : 0;

    return {
      totalEmployees,
      totalHours: formatHours(totalHours),
      averageHours: formatHours(averageHours),
    };
  }, [filteredUsers]);

  const fetchDashboardUsers = useCallback(async () => {
    const requestSequence = permissionRequestSequenceRef.current + 1;
    permissionRequestSequenceRef.current = requestSequence;
    const cachedAccess = readTimesheetCache(DASHBOARD_ACCESS_CACHE_SCOPE);

    if (cachedAccess) {
      setDashboardAccess(cachedAccess);
      setPermissionLoading(false);
      setPermissionError("");
    }

    try {
      setPermissionLoading(!cachedAccess);
      setPermissionError("");
      const response = await fetchTimesheetDashboardUsers();
      if (permissionRequestSequenceRef.current !== requestSequence) return;
      setDashboardAccess(response);
      writeTimesheetCache(DASHBOARD_ACCESS_CACHE_SCOPE, {}, response);
    } catch (error) {
      if (permissionRequestSequenceRef.current !== requestSequence) return;

      console.error("Failed to load dashboard permission:", error);
      if (cachedAccess) return;
      setPermissionError(
        error?.response?.data?.message ||
          "ไม่สามารถโหลดการตั้งค่าการมองเห็น Dashboard ได้"
      );
      setDashboardAccess({
        canViewDashboard: false,
        scope: "self",
        data: [],
      });
      toast.error(
        error?.response?.data?.message ||
          "ไม่สามารถโหลดการตั้งค่าการมองเห็น Dashboard ได้"
      );
    } finally {
      if (permissionRequestSequenceRef.current === requestSequence) {
        setPermissionLoading(false);
      }
    }
  }, []);

  const fetchDashboardSummary = useCallback(async () => {
    const requestSequence = summaryRequestSequenceRef.current + 1;
    summaryRequestSequenceRef.current = requestSequence;
    if (!dashboardAccess.canViewDashboard) return;
    const cacheKey = {
      from: range.from,
      to: range.to,
      clientId: filters.clientId,
    };
    const cachedSummary = readTimesheetCache(
      DASHBOARD_SUMMARY_CACHE_SCOPE,
      cacheKey,
    );

    if (cachedSummary) {
      setSummary(cachedSummary);
      setSummaryLoading(false);
    }

    try {
      setSummaryLoading(!cachedSummary);
      const data = await fetchTimesheetDashboardSummary({
        from: range.from,
        to: range.to,
        clientId: filters.clientId,
      });
      if (summaryRequestSequenceRef.current !== requestSequence) return;
      const nextSummary = {
        range: data.range || null,
        totalUsers: data.totalUsers || 0,
        totalHours: data.totalHours || 0,
        users: Array.isArray(data.users) ? data.users : [],
      };
      setSummary(nextSummary);
      writeTimesheetCache(DASHBOARD_SUMMARY_CACHE_SCOPE, cacheKey, nextSummary);
    } catch (error) {
      if (summaryRequestSequenceRef.current !== requestSequence) return;

      console.error("Failed to load dashboard summary:", error);
      if (cachedSummary) return;
      toast.error(formatErrorMessage(error));
      setSummary({
        range: null,
        totalUsers: 0,
        totalHours: 0,
        users: [],
      });
    } finally {
      if (summaryRequestSequenceRef.current === requestSequence) {
        setSummaryLoading(false);
      }
    }
  }, [dashboardAccess.canViewDashboard, filters.clientId, range.from, range.to]);

  useEffect(() => {
    if (!dashboardAccess.canViewDashboard) return;

    let cachedClients = null;

    try {
      const cached = localStorage.getItem(DASHBOARD_CLIENTS_CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : null;
      const cachedAt = Number(parsed?.cachedAt);
      const now = Date.now();
      const cacheIsFresh =
        Number.isFinite(cachedAt) &&
        cachedAt <= now &&
        now - cachedAt < TIMESHEET_CLIENTS_CACHE_TTL;

      cachedClients = cacheIsFresh && Array.isArray(parsed?.clients)
        ? parsed.clients
        : null;

      if (cached && !cachedClients) {
        localStorage.removeItem(DASHBOARD_CLIENTS_CACHE_KEY);
      }
    } catch (error) {
      localStorage.removeItem(DASHBOARD_CLIENTS_CACHE_KEY);
    }

    if (cachedClients) setClients(cachedClients);

    listTimesheetClients()
      .then((data) => {
        const clientList = Array.isArray(data) ? data : [];
        setClients(clientList);
        localStorage.setItem(
          DASHBOARD_CLIENTS_CACHE_KEY,
          JSON.stringify({ clients: clientList, cachedAt: Date.now() })
        );
      })
      .catch((error) => {
        console.error("Failed to load dashboard clients:", error);
        if (!cachedClients) setClients([]);
      });
  }, [dashboardAccess.canViewDashboard]);

  const fetchDetail = useCallback(async () => {
    const requestSequence = detailRequestSequenceRef.current + 1;
    detailRequestSequenceRef.current = requestSequence;

    if (!dashboardAccess.canViewDashboard || !routeUserId) {
      setEmployeeDetail(null);
      setDetailError("");
      return;
    }
    const cacheKey = { userId: routeUserId, from: range.from, to: range.to };
    const cachedDetail = readTimesheetCache(
      DASHBOARD_DETAIL_CACHE_SCOPE,
      cacheKey,
    );

    if (cachedDetail) {
      setEmployeeDetail(cachedDetail);
      setDetailLoading(false);
      setDetailError("");
    }

    try {
      setDetailLoading(!cachedDetail);
      setDetailError("");
      const data = await fetchEmployeeTimesheetSummary(routeUserId, {
        from: range.from,
        to: range.to,
      });
      if (detailRequestSequenceRef.current !== requestSequence) return;
      setEmployeeDetail(data);
      writeTimesheetCache(DASHBOARD_DETAIL_CACHE_SCOPE, cacheKey, data);
    } catch (error) {
      if (detailRequestSequenceRef.current !== requestSequence) return;

      console.error("Failed to load employee timesheet detail:", error);
      if (cachedDetail) return;
      const message = formatErrorMessage(error);
      setEmployeeDetail(null);
      setDetailError(message);
      toast.error(message);
    } finally {
      if (detailRequestSequenceRef.current === requestSequence) {
        setDetailLoading(false);
      }
    }
  }, [dashboardAccess.canViewDashboard, range.from, range.to, routeUserId]);

  useEffect(() => {
    fetchDashboardUsers();
  }, [fetchDashboardUsers]);

  useEffect(() => {
    fetchDashboardSummary();
  }, [fetchDashboardSummary]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    setEmployeePage(1);
  }, [filters]);

  useEffect(() => {
    if (employeePage > employeePageCount) {
      setEmployeePage(employeePageCount);
    }
  }, [employeePage, employeePageCount]);

  useEffect(() => {
    if (routeUserId && employeeDetailRef.current) {
      employeeDetailRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [employeeDetail, detailLoading, routeUserId]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchDashboardUsers(), fetchDashboardSummary(), fetchDetail()]);
  }, [fetchDashboardSummary, fetchDashboardUsers, fetchDetail]);

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      const data = await fetchTimesheetDashboardExportData({
        from: range.from,
        to: range.to,
        clientId: filters.clientId,
        department: filters.department,
        team: filters.team,
        search: filters.search.trim(),
      });

      if (!Array.isArray(data?.rows) || data.rows.length === 0) {
        toast.info("ไม่มีข้อมูลสำหรับ Export ตามตัวกรองที่เลือก");
        return;
      }

      exportTimesheetWorkbook(data, {
        includeDateInHeader: viewMode === TIMESHEET_VIEW.MONTH,
      });
      toast.success("Export Excel สำเร็จ");
    } catch (error) {
      console.error("Failed to export timesheet dashboard:", error);
      toast.error(
        error?.response?.data?.message || "ไม่สามารถ Export Excel ได้"
      );
    } finally {
      setExporting(false);
    }
  }, [filters, range.from, range.to, viewMode]);

  const isOrganizationScope =
    isAdmin() ||
    ORGANIZATION_SCOPES.includes(String(dashboardAccess.scope || "").toLowerCase());

  if (permissionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 py-5 transition-colors duration-300">
        <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-14 text-center shadow-sm">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            กำลังตรวจสอบสิทธิ์การเข้าถึง Dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (!dashboardAccess.canViewDashboard) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 py-5 transition-colors duration-300">
        {permissionError ? (
          <div className="rounded-3xl border border-red-200 dark:border-red-900/50 bg-red-50/80 dark:bg-red-500/10 px-6 py-14 text-center shadow-sm">
            <h1 className="text-xl font-semibold text-red-700 dark:text-red-300">
              Dashboard
            </h1>
            <p className="mt-3 text-sm text-red-600 dark:text-red-200">
              {permissionError}
            </p>
          </div>
        ) : (
          <div className="rounded-3xl border border-red-200 dark:border-red-900/50 bg-red-50/80 dark:bg-red-500/10 px-6 py-14 text-center shadow-sm">
            <h1 className="text-xl font-semibold text-red-700 dark:text-red-300">
              Dashboard
            </h1>
            <p className="mt-3 text-sm text-red-600 dark:text-red-200">
              คุณไม่มีสิทธิ์ดู Timesheet นี้
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 py-5 transition-colors duration-300">
      <div className="rounded-3xl border border-indigo-100 dark:border-indigo-900/40 bg-gradient-to-br from-white via-indigo-50/50 to-sky-50/70 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-6 sm:p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
            <FiBarChart2 size={22} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
              {isOrganizationScope
                ? "ภาพรวม Timesheet ทั้งองค์กร"
                : "ภาพรวม Timesheet ของทีม"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm sm:text-base text-gray-600 dark:text-gray-400">
              ดูชั่วโมงทำงานของสมาชิกในทีม แยกตามลูกค้าและ Project พร้อม Export เป็น Excel
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6">
        <TimesheetDashboardToolbar
          viewMode={viewMode}
          rangeLabel={range.label}
          loading={summaryLoading || detailLoading}
          onViewChange={setViewMode}
          onPrevious={() =>
            setAnchorDate((current) => shiftAnchorDate(viewMode, current, -1))
          }
          onNext={() =>
            setAnchorDate((current) => shiftAnchorDate(viewMode, current, 1))
          }
          onRefresh={handleRefresh}
          onExport={handleExport}
          exporting={exporting}
        />

        <TimesheetSummaryCards {...filteredMetrics} />

        <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <label className="relative">
              <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                ค้นหาพนักงาน
              </span>
              <FiSearch className="pointer-events-none absolute left-3 top-[43px] text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, search: event.target.value }))
                }
                placeholder="ค้นหาด้วยชื่อผู้ใช้"
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2.5 pl-10 pr-3 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </label>

            <label>
              <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                ลูกค้า
              </span>
              <select
                value={filters.clientId}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    clientId: event.target.value,
                  }))
                }
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-300"
              >
                <option value="">ลูกค้าทุกราย</option>
                {clients.map((client) => {
                  const clientId = getEntityId(client._id || client.id);
                  return (
                    <option key={clientId} value={clientId}>
                      {client.customerName || client.name || "-"}
                    </option>
                  );
                })}
              </select>
            </label>

            <label>
              <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                แผนก
              </span>
              <select
                value={filters.department}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    department: event.target.value,
                  }))
                }
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-300"
              >
                <option value="">ทุกแผนก</option>
                {visibleUserOptions.departments.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Team
              </span>
              <select
                value={filters.team}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, team: event.target.value }))
                }
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-300"
              >
                <option value="">ทุก Team</option>
                {visibleUserOptions.teams.map((team) => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </select>
            </label>

          </div>
        </div>

        <EmployeeTimesheetTable
          rows={pagedUsers}
          loading={summaryLoading}
          selectedUserId={routeUserId}
          page={employeePage}
          pageCount={employeePageCount}
          totalRows={filteredUsers.length}
          onPageChange={setEmployeePage}
          onViewEmployee={(userId) => navigate(`/timesheets/dashboard/${userId}`)}
        />

        {(routeUserId || detailLoading || detailError) && (
          <div ref={employeeDetailRef}>
            <EmployeeTimesheetDetail
              detail={employeeDetail}
              days={range.days}
              viewMode={viewMode}
              loading={detailLoading}
              errorMessage={detailError}
              onBack={() => navigate("/timesheets/dashboard")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetDashboardPage;
